import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { reportsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { 
  Loader2,
  Download,
  BarChart3,
  PieChart,
  TrendingUp,
  Calendar,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Settings,
  FileSpreadsheet,
  FileDown,
  Filter,
  X,
  Globe,
  FileText,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

export default function ReportsPage() {
  const { isSuperAdmin, organizationName } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Customization state
  const [showCustomize, setShowCustomize] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedSections, setSelectedSections] = useState({
    status: true,
    decision: true,
    service: true,
    country: true,
    county: true,
    cases: true
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await reportsAPI.getStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const getSelectedSectionsString = () => {
    return Object.entries(selectedSections)
      .filter(([_, selected]) => selected)
      .map(([key]) => key)
      .join(',');
  };

  const handleExportCSV = async (reportType = 'summary') => {
    setExporting(true);
    try {
      const options = {};
      if (dateFrom) options.date_from = dateFrom;
      if (dateTo) options.date_to = dateTo;
      if (reportType === 'summary') {
        options.sections = getSelectedSectionsString();
      }
      
      const response = await reportsAPI.exportReport(reportType, options);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${reportType}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV report downloaded');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const options = {
        sections: getSelectedSectionsString()
      };
      if (dateFrom) options.date_from = dateFrom;
      if (dateTo) options.date_to = dateTo;
      
      const response = await reportsAPI.exportPDF(options);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF report downloaded');
    } catch (error) {
      toast.error('PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleSectionToggle = (section) => {
    setSelectedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleClearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedSections({
      status: true,
      decision: true,
      service: true,
      country: true,
      county: true,
      cases: true
    });
  };

  // Color helpers
  const getStatusColor = (status) => {
    const colors = {
      'Active': '#059669',
      'Pending': '#e68a00',
      'Approved': '#1f3b70',
      'Denied': '#DC2626',
    };
    return colors[status] || '#64748B';
  };

  const chartColors = ['#1f3b70', '#e68a00', '#059669', '#64748B', '#DC2626', '#3B82F6', '#8B5CF6', '#06B6D4'];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const maxServiceCount = Math.max(...Object.values(stats?.cases_by_service_type || {}), 1);
  const maxCountryCount = Math.max(...Object.values(stats?.cases_by_country || {}), 1);
  const maxCountyCount = Math.max(...Object.values(stats?.cases_by_county || {}), 1);
  const hasFilters = dateFrom || dateTo || Object.values(selectedSections).some(v => !v);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />

      {/* Main Content */}
      <main className="ml-0 md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground tracking-tight">
              Reports & Analytics
            </h1>
            <p className="font-body text-muted-foreground">
              {isSuperAdmin ? 'Analytics across all organizations' : `Analytics for ${organizationName}`}
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="font-body"
              onClick={() => setShowCustomize(!showCustomize)}
              data-testid="customize-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Customize
              {hasFilters && <span className="ml-2 w-2 h-2 bg-primary rounded-full"></span>}
            </Button>
          </div>
        </div>

        {/* Customization Panel */}
        {showCustomize && (
          <Card className="mb-6 border-primary/20" data-testid="customize-panel">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Filter className="w-5 h-5 text-primary" />
                  Customize Report
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowCustomize(false)}
                  data-testid="close-customize-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <CardDescription className="font-body">
                Filter by date range and select which sections to include in your report
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Date Range */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-body text-sm font-medium">From Date</Label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                    data-testid="date-from-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-body text-sm font-medium">To Date</Label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                    data-testid="date-to-input"
                  />
                </div>
              </div>

              {/* Section Selection */}
              <div className="space-y-3">
                <Label className="font-body text-sm font-medium">Include Sections</Label>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  {[
                    { key: 'status', label: 'Cases by Status', icon: PieChart },
                    { key: 'decision', label: 'USCIS Decisions', icon: BarChart3 },
                    { key: 'service', label: 'Service Types', icon: FileText },
                    { key: 'country', label: 'Countries', icon: Globe },
                    { key: 'county', label: 'Counties', icon: MapPin },
                    { key: 'cases', label: 'Case Details', icon: Users }
                  ].map(({ key, label, icon: Icon }) => (
                    <div 
                      key={key}
                      className="flex items-center space-x-2 p-3 border border-border rounded-lg hover:bg-secondary/50 transition-colors cursor-pointer"
                      onClick={() => handleSectionToggle(key)}
                      data-testid={`section-${key}`}
                    >
                      <Checkbox 
                        id={key} 
                        checked={selectedSections[key]}
                        onCheckedChange={() => handleSectionToggle(key)}
                      />
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                        <Label htmlFor={key} className="font-body text-xs cursor-pointer">
                          {label}
                        </Label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Export Buttons */}
              <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-border">
                <span className="font-body text-sm text-muted-foreground mr-2">Download as:</span>
                <Button 
                  variant="outline" 
                  className="font-body"
                  onClick={() => handleExportCSV('summary')}
                  disabled={exporting}
                  data-testid="export-csv-summary-btn"
                >
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                  CSV Summary
                </Button>
                <Button 
                  variant="outline" 
                  className="font-body"
                  onClick={() => handleExportCSV('full')}
                  disabled={exporting}
                  data-testid="export-csv-full-btn"
                >
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                  CSV Full Data
                </Button>
                <Button 
                  className="btn-lift bg-primary text-white font-body"
                  onClick={handleExportPDF}
                  disabled={exporting}
                  data-testid="export-pdf-btn"
                >
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                  PDF Report
                </Button>
                {hasFilters && (
                  <Button 
                    variant="ghost" 
                    className="font-body text-muted-foreground"
                    onClick={handleClearFilters}
                    data-testid="clear-filters-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Clear Filters
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Export Buttons (when customize is closed) */}
        {!showCustomize && (
          <div className="flex gap-3 mb-6">
            <Button 
              variant="outline" 
              className="font-body"
              onClick={() => handleExportCSV('full')}
              disabled={exporting}
              data-testid="quick-export-csv-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Export CSV
            </Button>
            <Button 
              className="btn-lift bg-primary text-white font-body"
              onClick={handleExportPDF}
              disabled={exporting}
              data-testid="quick-export-pdf-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              Export PDF
            </Button>
          </div>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="card-hover" data-testid="stat-total">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-body text-sm font-medium text-muted-foreground">
                Total Cases
              </CardTitle>
              <Users className="w-5 h-5 text-primary" strokeWidth={1.5} />
            </CardHeader>
            <CardContent>
              <p className="font-heading text-3xl font-bold text-foreground">
                {stats?.summary?.total_cases || 0}
              </p>
              <p className="font-body text-xs text-muted-foreground mt-1">
                {stats?.summary?.active_cases || 0} active
              </p>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-approved">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-body text-sm font-medium text-muted-foreground">
                Approved
              </CardTitle>
              <CheckCircle className="w-5 h-5 text-success" strokeWidth={1.5} />
            </CardHeader>
            <CardContent>
              <p className="font-heading text-3xl font-bold text-success">
                {stats?.summary?.approved || 0}
              </p>
              <p className="font-body text-xs text-muted-foreground mt-1">
                {stats?.summary?.approval_rate || 0}% approval rate
              </p>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-pending">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-body text-sm font-medium text-muted-foreground">
                Pending Decision
              </CardTitle>
              <Clock className="w-5 h-5 text-warning" strokeWidth={1.5} />
            </CardHeader>
            <CardContent>
              <p className="font-heading text-3xl font-bold text-warning">
                {stats?.summary?.pending || 0}
              </p>
              <p className="font-body text-xs text-muted-foreground mt-1">
                Awaiting USCIS decision
              </p>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-denied">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-body text-sm font-medium text-muted-foreground">
                Denied
              </CardTitle>
              <XCircle className="w-5 h-5 text-error" strokeWidth={1.5} />
            </CardHeader>
            <CardContent>
              <p className="font-heading text-3xl font-bold text-error">
                {stats?.summary?.denied || 0}
              </p>
              <p className="font-body text-xs text-muted-foreground mt-1">
                USCIS denied cases
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Cases by Status - Donut Chart */}
          <Card data-testid="chart-status">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <PieChart className="w-5 h-5 text-primary" />
                Cases by Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center">
                <div className="relative w-48 h-48">
                  {/* Simple donut visualization */}
                  <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                    {Object.entries(stats?.cases_by_status || {}).reduce((acc, [status, count], idx) => {
                      const total = Object.values(stats?.cases_by_status || {}).reduce((a, b) => a + b, 0);
                      const percentage = total > 0 ? (count / total) * 100 : 0;
                      const offset = acc.offset;
                      acc.elements.push(
                        <circle
                          key={status}
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke={getStatusColor(status)}
                          strokeWidth="20"
                          strokeDasharray={`${percentage * 2.51} 251`}
                          strokeDashoffset={-offset * 2.51}
                          className="transition-all duration-500"
                        />
                      );
                      acc.offset += percentage;
                      return acc;
                    }, { elements: [], offset: 0 }).elements}
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <p className="font-heading text-2xl font-bold text-foreground">
                        {stats?.summary?.total_cases || 0}
                      </p>
                      <p className="font-body text-xs text-muted-foreground">Total</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Legend */}
              <div className="flex flex-wrap justify-center gap-4 mt-4">
                {Object.entries(stats?.cases_by_status || {}).map(([status, count]) => (
                  <div key={status} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: getStatusColor(status) }}
                    />
                    <span className="font-body text-sm text-muted-foreground">
                      {status} ({count})
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Cases by USCIS Decision */}
          <Card data-testid="chart-decision">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                USCIS Decisions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(stats?.cases_by_decision || {}).map(([decision, count]) => {
                  const total = Object.values(stats?.cases_by_decision || {}).reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? (count / total) * 100 : 0;
                  return (
                    <div key={decision}>
                      <div className="flex justify-between mb-1">
                        <span className="font-body text-sm text-foreground">{decision}</span>
                        <span className="font-body text-sm text-muted-foreground">{count} ({percentage.toFixed(0)}%)</span>
                      </div>
                      <div className="h-3 bg-secondary rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ 
                            width: `${percentage}%`,
                            backgroundColor: getStatusColor(decision)
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Service Type & Country Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Cases by Service Type */}
          <Card data-testid="chart-service">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Cases by Service Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(stats?.cases_by_service_type || {})
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 8)
                  .map(([service, count], idx) => (
                    <div key={service} className="flex items-center gap-3">
                      <div className="w-24 truncate font-body text-sm text-foreground">{service}</div>
                      <div className="flex-1 h-6 bg-secondary rounded overflow-hidden">
                        <div 
                          className="h-full rounded transition-all duration-500 flex items-center justify-end pr-2"
                          style={{ 
                            width: `${(count / maxServiceCount) * 100}%`,
                            backgroundColor: chartColors[idx % chartColors.length]
                          }}
                        >
                          <span className="font-body text-xs text-white font-medium">{count}</span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>

          {/* Cases by Country */}
          <Card data-testid="chart-country">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary" />
                Cases by Country
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(stats?.cases_by_country || {})
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 8)
                  .map(([country, count], idx) => (
                    <div key={country} className="flex items-center gap-3">
                      <div className="w-24 truncate font-body text-sm text-foreground">{country}</div>
                      <div className="flex-1 h-6 bg-secondary rounded overflow-hidden">
                        <div 
                          className="h-full rounded transition-all duration-500 flex items-center justify-end pr-2"
                          style={{ 
                            width: `${(count / maxCountryCount) * 100}%`,
                            backgroundColor: chartColors[idx % chartColors.length]
                          }}
                        >
                          <span className="font-body text-xs text-white font-medium">{count}</span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* County Chart */}
        <div className="grid grid-cols-1 gap-6 mb-8">
          <Card data-testid="chart-county">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                Cases by County
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(stats?.cases_by_county || {}).length === 0 ||
               (Object.keys(stats?.cases_by_county || {}).length === 1 && (stats?.cases_by_county || {})["Not Specified"]) ? (
                <p className="font-body text-muted-foreground text-center py-8">
                  No county data available. Add county information to cases to see this breakdown.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                  {Object.entries(stats?.cases_by_county || {})
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 12)
                    .map(([county, count], idx) => (
                      <div key={county} className="flex items-center gap-3">
                        <div className="w-28 truncate font-body text-sm text-foreground">{county}</div>
                        <div className="flex-1 h-6 bg-secondary rounded overflow-hidden">
                          <div 
                            className="h-full rounded transition-all duration-500 flex items-center justify-end pr-2"
                            style={{ 
                              width: `${(count / maxCountyCount) * 100}%`,
                              backgroundColor: chartColors[idx % chartColors.length]
                            }}
                          >
                            <span className="font-body text-xs text-white font-medium">{count}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Cases Over Time & Upcoming */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Cases Over Time */}
          <Card data-testid="chart-timeline">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Cases Over Time (12 Months)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48 flex items-end gap-1">
                {stats?.cases_over_time?.map((month, idx) => {
                  const maxCount = Math.max(...(stats?.cases_over_time?.map(m => m.count) || [1]), 1);
                  const height = maxCount > 0 ? (month.count / maxCount) * 100 : 0;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                      <div 
                        className="w-full bg-primary rounded-t transition-all duration-500 min-h-[4px]"
                        style={{ height: `${Math.max(height, 2)}%` }}
                        title={`${month.month}: ${month.count} cases`}
                      />
                      <span className="font-body text-[10px] text-muted-foreground -rotate-45 origin-left">
                        {month.month.split(' ')[0]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Biometrics */}
          <Card data-testid="upcoming-biometrics">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                Upcoming Biometrics (30 Days)
              </CardTitle>
              <CardDescription className="font-body">
                Clients with biometrics appointments coming up
              </CardDescription>
            </CardHeader>
            <CardContent>
              {stats?.upcoming_biometrics?.length === 0 ? (
                <p className="font-body text-muted-foreground text-center py-8">
                  No upcoming biometrics appointments
                </p>
              ) : (
                <div className="space-y-3">
                  {stats?.upcoming_biometrics?.slice(0, 5).map((item) => (
                    <Link 
                      key={item.id} 
                      to={`/cases/${item.id}`}
                      className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-secondary/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-warning/10 flex items-center justify-center">
                          <Calendar className="w-4 h-4 text-warning" />
                        </div>
                        <div>
                          <p className="font-body font-medium text-foreground">{item.name}</p>
                          <p className="font-body text-xs text-muted-foreground">{item.biometrics_date}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-body font-medium ${
                        item.days_until <= 7 ? 'bg-error/10 text-error' : 'bg-warning/10 text-warning'
                      }`}>
                        {item.days_until === 0 ? 'Today' : `${item.days_until} days`}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Cases */}
        <Card data-testid="recent-cases">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Recent Cases
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_cases?.length === 0 ? (
              <p className="font-body text-muted-foreground text-center py-8">
                No cases yet
              </p>
            ) : (
              <div className="space-y-3">
                {stats?.recent_cases?.map((caseItem) => (
                  <Link 
                    key={caseItem.id} 
                    to={`/cases/${caseItem.id}`}
                    className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-secondary/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="font-heading text-sm font-semibold text-primary">
                          {caseItem.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="font-body font-medium text-foreground">{caseItem.name}</p>
                        <p className="font-body text-xs text-muted-foreground">
                          {caseItem.type_of_services || 'No service type'} • {new Date(caseItem.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-body font-medium`} style={{
                      backgroundColor: `${getStatusColor(caseItem.status)}20`,
                      color: getStatusColor(caseItem.status)
                    }}>
                      {caseItem.status}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
